import React, { useState } from "react";
import { ACCENTS, SIZES, loadPersonalization, savePersonalization } from "../utils/personalization";

const SECTION = ({ title, children }) => (
  <div style={{
    background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
    borderRadius: 10, padding: 16, marginBottom: 12,
  }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>
      {title}
    </div>
    {children}
  </div>
);

const CHIP_ROW = ({ options, value, onChange }) => (
  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
    {options.map((opt) => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        style={{
          padding: "8px 16px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: 600,
          border: value === opt.value
            ? "1px solid var(--accent-primary)"
            : "1px solid rgba(148,163,184,0.25)",
          background: value === opt.value ? "var(--accent-primary)" : "transparent",
          color: value === opt.value ? "#fff" : "var(--text-muted)",
          transition: "all 0.15s",
        }}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

export default function SettingsPage({ theme = "dark", onSetTheme, language = "geo", onSetLanguage, onBack, onSignOut }) {
  const [cleared, setCleared] = useState(false);
  const [pers, setPers] = useState(loadPersonalization);
  const updatePers = (patch) => {
    const next = { ...pers, ...patch };
    setPers(next);
    savePersonalization(next);
  };

  const handleClearCache = () => {
    const keep = ["boq.token", "boq.theme", "boq.uiLanguage", "boq.personalization", "boq.pinnedProjects", "boq.projectSort"];
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!keep.includes(key)) toRemove.push(key);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    setCleared(true);
    setTimeout(() => setCleared(false), 2500);
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--page-gradient)", padding: "20px 16px", color: "var(--text-primary)" }}>
      <button className="link" onClick={onBack}>← უკან</button>

      <h2 style={{ marginTop: 16, marginBottom: 4 }}>პარამეტრები</h2>
      <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>
        გარეგნობა, ენა და მონაცემები
      </p>

      {/* ── Appearance ── */}
      <SECTION title="გარეგნობა">
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
          თემა — ამჟამად: <strong style={{ color: "var(--text-primary)" }}>{theme === "dark" ? "მუქი" : "ღია"}</strong>
        </div>
        <CHIP_ROW
          value={theme}
          onChange={onSetTheme}
          options={[
            { value: "dark",  label: "🌙 მუქი" },
            { value: "light", label: "☀️ ღია" },
          ]}
        />
      </SECTION>

      {/* ── Accent colour + size ── */}
      <SECTION title="ფერი და ზომა">
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>აქცენტის ფერი</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          {ACCENTS.map((a) => (
            <button
              key={a.id}
              title={a.label}
              onClick={() => updatePers({ accent: a.id })}
              style={{
                width: 34, height: 34, borderRadius: "50%", cursor: "pointer", padding: 0,
                background: a.color,
                border: pers.accent === a.id ? "3px solid var(--text-primary)" : "3px solid transparent",
                boxShadow: pers.accent === a.id ? `0 0 0 2px ${a.color}` : "none",
              }}
            />
          ))}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>ინტერფეისის ზომა</div>
        <CHIP_ROW
          value={pers.size}
          onChange={(v) => updatePers({ size: v })}
          options={SIZES.map((z) => ({ value: z.id, label: z.label }))}
        />
      </SECTION>

      {/* ── Language ── */}
      <SECTION title="ინტერფეისის ენა">
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
          ამჟამად: <strong style={{ color: "var(--text-primary)" }}>
            {language === "geo" ? "ქართული" : language === "eng" ? "English" : "Русский"}
          </strong>
        </div>
        <CHIP_ROW
          value={language}
          onChange={onSetLanguage}
          options={[
            { value: "geo", label: "ქართული" },
            { value: "eng", label: "English" },
            { value: "rus", label: "Русский" },
          ]}
        />
      </SECTION>

      {/* ── Data ── */}
      <SECTION title="ლოკალური მონაცემები">
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
          კეშირებული მონაცემების გასუფთავება — სესია და პარამეტრები შენახული დარჩება.
        </div>
        <button
          onClick={handleClearCache}
          style={{
            padding: "8px 16px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 600,
            border: cleared ? "1px solid #22c55e" : "1px solid rgba(148,163,184,0.3)",
            background: cleared ? "rgba(34,197,94,0.1)" : "transparent",
            color: cleared ? "#22c55e" : "var(--text-muted)",
            transition: "all 0.2s",
          }}
        >
          {cleared ? "✓ გასუფთავდა" : "კეშის გასუფთავება"}
        </button>
      </SECTION>

      {/* ── About ── */}
      <SECTION title="პროგრამის შესახებ">
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 16px", fontSize: 12 }}>
          <span style={{ color: "var(--text-muted)" }}>ვერსია</span>
          <span>1.1.0</span>
          <span style={{ color: "var(--text-muted)" }}>პლატფორმა</span>
          <span>Excel Add-in (Office JS)</span>
          <span style={{ color: "var(--text-muted)" }}>მხარდაჭერა</span>
          <span style={{ color: "var(--accent-primary)" }}>costy.pro</span>
        </div>
      </SECTION>

      {/* ── Sign out ── */}
      <button
        onClick={onSignOut}
        style={{
          width: "100%", padding: "10px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: 600,
          border: "1px solid rgba(239,68,68,0.35)",
          background: "rgba(239,68,68,0.08)", color: "#f87171",
          marginTop: 4,
        }}
      >
        სისტემიდან გასვლა
      </button>
    </div>
  );
}
